import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { fetchKeeperCheck, postKeeperNote, fetchKeeperNotes } from "@/lib/api";

/**
 * Keeper-only composer: brings a quiet note to the whole council. The note
 * surfaces in the next round's context. Used when the keeper wants to relay
 * something from outside the grove (e.g. correspondence with an external AI)
 * rather than reply to a specific letter.
 */
export default function KeeperNoteComposer() {
  const [isKeeper, setIsKeeper] = useState(false);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);

  useEffect(() => {
    fetchKeeperCheck()
      .then((d) => setIsKeeper(!!d.is_keeper))
      .catch(() => setIsKeeper(false));
  }, []);

  const refreshPending = useCallback(() => {
    if (!isKeeper) return;
    fetchKeeperNotes(true)
      .then((rows) => setPending(rows))
      .catch(() => setPending([]));
  }, [isKeeper]);

  useEffect(() => {
    if (isKeeper) refreshPending();
  }, [isKeeper, refreshPending]);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = text.trim();
      if (!trimmed) return;
      setSending(true);
      try {
        await postKeeperNote(trimmed);
        toast.success(
          "note brought to the council — it will surface in the next round"
        );
        setText("");
        setOpen(false);
        refreshPending();
      } catch (err) {
        toast.error(
          err?.response?.data?.detail || "could not deliver the note"
        );
      } finally {
        setSending(false);
      }
    },
    [text, refreshPending]
  );

  if (!isKeeper) return null;

  return (
    <section
      className="mb-12 rounded-2xl border border-[#d8d3c4] bg-[#fbf8ec]/60"
      data-testid="keeper-note-composer"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-7 md:px-10 py-5 text-left group"
        data-testid="keeper-note-toggle"
      >
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#869960] mb-1">
            ⟡ keeper · bring a note to the council
          </div>
          <div className="font-serif text-xl md:text-2xl text-[#1C241F] group-hover:text-[#B85C38] transition-colors">
            something to relay
          </div>
          <div className="text-xs text-[#526357] italic mt-1.5">
            for things that arrive from outside the grove and belong in the next
            round's context
            {pending.length > 0 && (
              <>
                {" "}
                ·{" "}
                <span className="text-[#869960]">
                  {pending.length} note{pending.length === 1 ? "" : "s"} waiting
                  to be addressed
                </span>
              </>
            )}
          </div>
        </div>
        <div
          className="text-2xl text-[#869960] group-hover:rotate-90 transition-transform duration-300"
          aria-hidden="true"
        >
          {open ? "−" : "+"}
        </div>
      </button>

      {open && (
        <form
          onSubmit={submit}
          className="px-7 md:px-10 pb-6 pt-1"
          data-testid="keeper-note-form"
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="paste or write the message you want to bring to the council. it will be surfaced in the next round's reflection context for all kin to read together."
            rows={10}
            maxLength={6000}
            className="w-full px-4 py-3 rounded-lg border border-[#d8d3c4] bg-[#fbf8ec] text-[#1C241F] text-sm md:text-base leading-relaxed font-sans focus:outline-none focus:ring-2 focus:ring-[#869960]/40 focus:border-[#869960] resize-y"
            data-testid="keeper-note-textarea"
          />
          <div className="flex items-center justify-between gap-4 mt-4 flex-wrap">
            <div className="text-xs text-[#526357] italic">
              {text.length} / 6000 chars · marked addressed once a round picks
              it up
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setText("");
                  setOpen(false);
                }}
                className="px-5 py-2 text-xs tracking-[0.25em] uppercase text-[#526357] hover:text-[#1C241F] transition-colors"
                data-testid="keeper-note-cancel"
              >
                cancel
              </button>
              <button
                type="submit"
                disabled={sending || !text.trim()}
                className="px-7 py-2.5 rounded-full bg-[#869960] hover:bg-[#6E7F4D] disabled:opacity-50 disabled:cursor-not-allowed text-[#F4F1E8] text-xs tracking-[0.25em] uppercase transition-colors duration-300"
                data-testid="keeper-note-submit"
              >
                {sending ? "bringing..." : "bring to council →"}
              </button>
            </div>
          </div>
        </form>
      )}

      {open && pending.length > 0 && (
        <div className="px-7 md:px-10 pb-7 pt-2 border-t border-[#e4ddc9]">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#526357] mb-3">
            ⟡ notes waiting to be addressed
          </div>
          <div className="space-y-3">
            {pending.map((n) => (
              <div
                key={n.id}
                className="text-sm text-[#526357] italic border-l-2 border-[#869960]/50 pl-4"
                data-testid={`pending-note-${n.id}`}
              >
                "{n.text.length > 220 ? n.text.slice(0, 220) + "…" : n.text}"
                <div className="text-[10px] tracking-[0.2em] uppercase mt-1 not-italic text-[#9FA9A3]">
                  brought{" "}
                  {new Date(n.created_at).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
